import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography,
} from "@mui/material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useLinkedIn } from "react-linkedin-login-oauth2";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import Sidebar from "../components/Sidebar";
import { selectUser } from "../redux/slices/authSlice";
import {
  LINKEDIN_CLIENT_ID,
  LINKEDIN_REDIRECT_URL,
  LINKEDIN_SCOPES,
} from "../config/secrets";

const API_BASE =
  process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4009";

/** CRA dotenv may keep surrounding quotes — strip them like RP expects plain values. */
const cleanEnv = (v) => (v || "").replace(/^"|"$/g, "").trim();

const formatExpiry = (value) => {
  if (!value) return "—";
  const d = dayjs(value);
  if (!d.isValid()) return String(value);
  return d.format("MMM D, YYYY [at] h:mm A");
};

const ConnectSocials = () => {
  const user = useSelector(selectUser);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [status, setStatus] = useState(null);
  const [available, setAvailable] = useState(null);
  const [selected, setSelected] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  // Match other SA pages (Drawer paper is fixed; content shifts with margin only).
  const sidebarWidth = sidebarOpen ? 220 : 100;

  // Match RP: secrets → env (localhost:3002/linkedin for local SA).
  const linkedInRedirectUri =
    cleanEnv(LINKEDIN_REDIRECT_URL) ||
    `${window.location.origin}/linkedin`;
  const linkedInClientId = cleanEnv(LINKEDIN_CLIENT_ID);
  const linkedInScopes = cleanEnv(LINKEDIN_SCOPES)
    .replace(/%20|[,+]/gi, " ")
    .split(/\s+/)
    .filter(Boolean)
    .join(" ");

  const linkedin = status?.linkedin;
  const isConnected = Boolean(status?.connected);
  const hasToken = Boolean(linkedin?.accessTokenExpiry);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      await fetch(`${API_BASE}/api/linkedin/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force: false }),
      }).catch(() => null);

      const res = await fetch(`${API_BASE}/api/linkedin/status`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load status");
      setStatus(data);
      const pages = data?.linkedin?.pages || [];
      setSelected(pages.map((p) => p.ugn).filter(Boolean));
      return data;
    } catch (err) {
      setError(err.message || "Failed to load LinkedIn status");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPages = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/linkedin/pages`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load pages");
      setAvailable(data);
      setSelectMode(true);
      return data;
    } catch (err) {
      setError(err.message || "Failed to load LinkedIn pages");
      return null;
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Same pattern as RP ConnectSocialsModal: useLinkedIn → onSuccess(code) → API.
  const { linkedInLogin } = useLinkedIn({
    clientId: linkedInClientId,
    redirectUri: linkedInRedirectUri,
    scope: linkedInScopes,
    onSuccess: async (code) => {
      setBusy(true);
      setError("");
      setSuccess("");
      try {
        const res = await fetch(`${API_BASE}/api/linkedin/connect`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            adminUserId: user?.id || user?._id || user?.email || null,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || "Connect failed");
        setSuccess("LinkedIn authorized. Select page(s) to finish.");
        await fetchStatus();
        // RP opens page picker after short delay; we load pages immediately.
        setTimeout(async () => {
          await loadPages();
        }, 500);
      } catch (err) {
        setError(err.message || "LinkedIn connect failed");
        setBusy(false);
      }
    },
    onError: (err) => {
      // RP ignores errors; v2 fires user_closed_popup after successful close too.
      if (err?.error === "user_closed_popup") return;
      setError(
        err?.errorMessage || "LinkedIn authorization was cancelled or failed",
      );
    },
  });

  const startLinkedInConnect = () => {
    setError("");
    setSuccess("");
    if (!linkedInClientId) {
      setError("REACT_APP_LINKEDIN_CLIENT_ID is missing");
      return;
    }
    if (!linkedInRedirectUri) {
      setError("REACT_APP_LINKEDIN_REDIRECT_URL is missing");
      return;
    }
    // Clear stale state from earlier failed attempts (same key RP uses).
    try {
      window.localStorage.removeItem("linkedin_oauth2_state");
    } catch (_) {
      /* ignore */
    }
    linkedInLogin();
  };

  const toggleUgn = (ugn) => {
    setSelected((prev) =>
      prev.includes(ugn) ? prev.filter((x) => x !== ugn) : [...prev, ugn],
    );
  };

  const savePages = async () => {
    if (!selected.length) {
      setError("Select at least one page or personal profile");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const now = new Date().toISOString();
      const pages = [];
      if (available?.user_data && selected.includes(available.user_data.ugn)) {
        pages.push({
          ugn: available.user_data.ugn,
          page_name: available.user_data.user_name,
          profile_picture: available.user_data.profile_picture,
          type: "profile",
          added_at: now,
        });
      }
      (available?.organization_data || []).forEach((org) => {
        if (selected.includes(org.ugn)) {
          pages.push({
            ugn: org.ugn,
            page_name: org.page_name,
            profile_picture: org.profile_picture,
            type: "page",
            added_at: now,
          });
        }
      });

      const res = await fetch(`${API_BASE}/api/linkedin/pages`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pages,
          adminUserId: user?.id || user?._id || user?.email || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to save pages");
      setSuccess("LinkedIn pages saved");
      setSelectMode(false);
      await fetchStatus();
    } catch (err) {
      setError(err.message || "Failed to save pages");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Getgeek LinkedIn from Super Admin?")) return;
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/linkedin/disconnect`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Disconnect failed");
      try {
        window.localStorage.removeItem("linkedin_oauth2_state");
      } catch (_) {
        /* ignore */
      }
      setSuccess("LinkedIn disconnected");
      setAvailable(null);
      setSelectMode(false);
      await fetchStatus();
    } catch (err) {
      setError(err.message || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  const statusColor = useMemo(() => {
    const s = status?.status || linkedin?.status;
    if (s === "active") return "success";
    if (s === "expiring") return "warning";
    if (s === "broken") return "error";
    return "default";
  }, [status, linkedin]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F7F8FA" }}>
      <Sidebar onSidebarStateChange={setSidebarOpen} />
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          ml: {
            xs: 0,
            md: `${sidebarWidth}px`,
          },
          transition: "margin-left 0.3s ease",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        <Typography variant="h5" fontWeight={700} mb={1}>
          Connect Socials
        </Typography>
        <Typography color="text.secondary" mb={3}>
          Connect Getgeek&apos;s official LinkedIn company page (tokens stored
          securely on contact@getgeek.ai auth document).
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {loading && !busy ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <LinkedInIcon sx={{ color: "#0A66C2", fontSize: 36 }} />
              <Box>
                <Typography fontWeight={700}>LinkedIn</Typography>
                <Chip
                  size="small"
                  label={
                    isConnected
                      ? status?.status || linkedin?.status || "connected"
                      : hasToken
                        ? status?.status || linkedin?.status || "authorized"
                        : "not connected"
                  }
                  color={isConnected || hasToken ? statusColor : "default"}
                  sx={{ mt: 0.5, textTransform: "capitalize" }}
                />
              </Box>
            </Box>

            {linkedin && (
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                  Member: {linkedin.memberName || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Organization: {linkedin.organizationUrn || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access token expiry: {formatExpiry(linkedin.accessTokenExpiry)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Refresh token expiry:{" "}
                  {formatExpiry(linkedin.refreshTokenExpiry)}
                </Typography>
                {linkedin.lastError && (
                  <Typography variant="body2" color="error" mt={1}>
                    {linkedin.lastError}
                  </Typography>
                )}
                {(linkedin.pages || []).length > 0 && (
                  <Box mt={1.5} display="flex" flexWrap="wrap" gap={1}>
                    {linkedin.pages.map((p) => (
                      <Chip
                        key={p.ugn}
                        avatar={<Avatar src={p.profile_picture} />}
                        label={`${p.page_name} (${p.type})`}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Box display="flex" flexWrap="wrap" gap={1.5}>
              <Button
                variant="contained"
                startIcon={<LinkedInIcon />}
                disabled={busy}
                onClick={startLinkedInConnect}
                sx={{ bgcolor: "#0A66C2", textTransform: "none" }}
              >
                {hasToken || isConnected
                  ? "Reconnect LinkedIn"
                  : "Connect LinkedIn"}
              </Button>

              {hasToken && (
                <Button
                  variant="outlined"
                  disabled={busy}
                  onClick={loadPages}
                  sx={{ textTransform: "none" }}
                >
                  Select / Update Pages
                </Button>
              )}

              {(isConnected || hasToken) && (
                <Button
                  color="error"
                  variant="outlined"
                  disabled={busy}
                  onClick={disconnect}
                  sx={{ textTransform: "none" }}
                >
                  Disconnect
                </Button>
              )}

              <Button
                variant="text"
                disabled={busy || loading}
                onClick={fetchStatus}
                sx={{ textTransform: "none" }}
              >
                Refresh Status
              </Button>
            </Box>
            {busy && (
              <Box mt={2} display="flex" alignItems="center" gap={1}>
                <CircularProgress size={22} />
                <Typography variant="body2" color="text.secondary">
                  Working…
                </Typography>
              </Box>
            )}
          </Paper>
        )}

        {selectMode && available && (
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography fontWeight={700} mb={2}>
              Select LinkedIn profile / pages
            </Typography>

            {available.user_data && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selected.includes(available.user_data.ugn)}
                    onChange={() => toggleUgn(available.user_data.ugn)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={available.user_data.profile_picture} />
                    <Box>
                      <Typography fontWeight={600}>
                        {available.user_data.user_name}
                      </Typography>
                      <Typography variant="caption">
                        Personal profile
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ display: "flex", mb: 1 }}
              />
            )}

            {(available.organization_data || []).map((org) => (
              <FormControlLabel
                key={org.ugn}
                control={
                  <Checkbox
                    checked={selected.includes(org.ugn)}
                    onChange={() => toggleUgn(org.ugn)}
                  />
                }
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar src={org.profile_picture} />
                    <Box>
                      <Typography fontWeight={600}>{org.page_name}</Typography>
                      <Typography variant="caption">Company page</Typography>
                    </Box>
                  </Box>
                }
                sx={{ display: "flex", mb: 1 }}
              />
            ))}

            {!available.user_data &&
              !(available.organization_data || []).length && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No LinkedIn profiles/pages were returned for this token.
                  Confirm the LinkedIn user administers the Getgeek company
                  page and that the app has organization scopes.
                </Alert>
              )}

            <Box display="flex" gap={1.5} mt={2}>
              <Button
                variant="contained"
                disabled={busy || !selected.length}
                onClick={savePages}
                sx={{ textTransform: "none" }}
              >
                Save selection
              </Button>
              <Button
                disabled={busy}
                onClick={() => setSelectMode(false)}
                sx={{ textTransform: "none" }}
              >
                Cancel
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default ConnectSocials;
